<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useFetchApplicationDetail } from '@/entities/application/model/useFetchApplicationDetail';
import { useFetchLuggageLocation } from '@/entities/application/model/useFetchLuggageLocation';
import ApplicationDetailContainer from './ui/ApplicationDetailContainer.vue';
import { BaggageQrScannerModal } from '@/features/baggageQrScan';
import { ErrorSnackbar } from '@/shared/ui';
import { useAddLuggage } from '@/features/application/addLuggage';
import { RemoveLuggageModal } from '@/features/application/removeLuggage';
import { ForceUpdateDeliveryStatusModal } from '@/features/application/forceUpdateDeliveryStatus';

const props = defineProps<{
    applicationId: string;
}>();

const router = useRouter();

const {
    detail: applicationDetail,
    errorMessage: detailError,
    isLoading: isDetailLoading,
    refetch: refetchApplicationDetail,
} = useFetchApplicationDetail();

const {
    locationData,
    errorMessage: locationError,
    isLoading: isLocationLoading,
    refresh: refreshLuggageLocation,
} = useFetchLuggageLocation();

const { addLuggageToApplication, errorMessage: addLuggageError } = useAddLuggage();

const showQrScannerModal = ref(false);
const showRemoveLuggageModal = ref(false);
const showForceUpdateStatusModal = ref(false);
const showPhotoModal = ref(false);
const showErrorSnackbar = ref(false);
const selectedPhotoUrl = ref('');
const selectedPhotoType = ref<'reception' | 'arrangement'>('arrangement');
const scannedQrCodes = ref<string[]>([]);
const existingLuggageQrCodes = computed(() => applicationDetail.value?.luggage_info?.luggage_qr_codes ?? []);
const allScannedQrCodes = computed(() => [...existingLuggageQrCodes.value, ...scannedQrCodes.value]);

const combinedErrorMessage = ref('');
if (detailError.value) {
    combinedErrorMessage.value = detailError.value;
} else if (locationError.value) {
    combinedErrorMessage.value = locationError.value;
}

const handleAddLuggage = () => {
    scannedQrCodes.value = [];
    showQrScannerModal.value = true;
};

const handleQrDetect = async (qrCode: string) => {
    const success = await addLuggageToApplication(props.applicationId, qrCode);
    if (success) {
        scannedQrCodes.value = [...scannedQrCodes.value, qrCode];
    } else {
        showErrorSnackbar.value = true;
    }
};

const handleRemoveLuggage = () => {
    showRemoveLuggageModal.value = true;
};

const handlePhotoClick = (photoUrl: string, photoType: 'reception' | 'arrangement') => {
    selectedPhotoUrl.value = photoUrl;
    selectedPhotoType.value = photoType;
    showPhotoModal.value = true;
};

const handleQrScannerClose = () => {
    if (scannedQrCodes.value.length > 0) {
        window.location.reload();
    }
};

const handleLuggageRemoved = () => {
    window.location.reload();
};

const closePhotoModal = () => {
    showPhotoModal.value = false;
    selectedPhotoUrl.value = '';
};

const handleNavigateConsent = () => {
    router.push({ name: 'application.traveler-consent', params: { applicationId: props.applicationId } });
};

const handleNavigateHandover = () => {
    router.push({ name: 'application.traveler-handover', params: { applicationId: props.applicationId } });
};

const handleForceUpdateStatus = () => {
    showForceUpdateStatusModal.value = true;
};

const handleStatusUpdated = () => {
    window.location.reload();
};

const handleUpdateCompleted = async () => {
    await Promise.all([refetchApplicationDetail(), refreshLuggageLocation()]);
    // スナックバーを表示
    updateSuccessMessage.value = '申込情報を更新しました';
    showUpdateSuccessSnackbar.value = true;
};

// メモ更新完了（メモパネルが表示を楽観的更新するため全体再取得は行わない）
const handleMemoUpdated = () => {
    updateSuccessMessage.value = 'メモを更新しました';
    showUpdateSuccessSnackbar.value = true;
};

// 更新完了スナックバー
const showUpdateSuccessSnackbar = ref(false);
const updateSuccessMessage = ref('申込情報を更新しました');
</script>

<template>
    <Teleport to="#header-title-slot" defer>
        <h1 class="header-title">ユーザー詳細：{{ applicationDetail?.customer_info?.user_name || '' }}</h1>
    </Teleport>

    <div class="application-detail-page">
        <ApplicationDetailContainer
            :application-detail="applicationDetail"
            :location-data="locationData"
            :is-loading="isDetailLoading || isLocationLoading"
            :error-message="combinedErrorMessage"
            @add-luggage="handleAddLuggage"
            @remove-luggage="handleRemoveLuggage"
            @photo-click="handlePhotoClick"
            @navigate-consent="handleNavigateConsent"
            @navigate-handover="handleNavigateHandover"
            @force-update-status="handleForceUpdateStatus"
            @update-completed="handleUpdateCompleted"
            @memo-updated="handleMemoUpdated"
        />

        <BaggageQrScannerModal
            v-model:is-open="showQrScannerModal"
            :scanned-items="allScannedQrCodes"
            @detect="handleQrDetect"
            @update:is-open="(val) => !val && handleQrScannerClose()"
        />

        <RemoveLuggageModal
            v-model="showRemoveLuggageModal"
            :application-id="applicationId"
            :current-luggage-qr-codes="applicationDetail?.luggage_info?.luggage_qr_codes || []"
            @luggage-removed="handleLuggageRemoved"
        />

        <ForceUpdateDeliveryStatusModal
            v-model="showForceUpdateStatusModal"
            :application-id="applicationId"
            :current-status="applicationDetail?.delivery_status"
            @status-updated="handleStatusUpdated"
        />

        <ErrorSnackbar v-model="showErrorSnackbar">
            {{ addLuggageError }}
        </ErrorSnackbar>

        <v-snackbar v-model="showUpdateSuccessSnackbar" :timeout="3000" location="top" color="success">
            {{ updateSuccessMessage }}
        </v-snackbar>

        <v-dialog v-model="showPhotoModal" max-width="800">
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>{{ selectedPhotoType === 'reception' ? '荷物写真' : '置き配写真' }}</span>
                    <v-btn icon="mdi-close" variant="text" size="small" @click="closePhotoModal" />
                </v-card-title>
                <v-card-text class="pa-0">
                    <v-img
                        :src="selectedPhotoUrl"
                        :alt="selectedPhotoType === 'reception' ? '荷物写真' : '置き配写真'"
                        max-height="600"
                        contain
                    >
                        <template #placeholder>
                            <div class="d-flex align-center justify-center h-100">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                        </template>
                        <template #error>
                            <div class="d-flex flex-column align-center justify-center h-100 pa-4">
                                <v-icon size="64" color="grey" class="mb-2">mdi-image-broken</v-icon>
                                <p class="text-body-1 text-grey">画像を読み込めませんでした</p>
                            </div>
                        </template>
                    </v-img>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        variant="outlined"
                        :href="selectedPhotoUrl"
                        target="_blank"
                        prepend-icon="mdi-open-in-new"
                    >
                        新しいタブで開く
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<style scoped>
.application-detail-page {
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    background-color: #fafafa;
    padding: 20px;
    box-sizing: border-box;
}

.page-header {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-left: 4px solid #1976d2;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
    .application-detail-page {
        padding: 12px;
    }

    .page-header {
        padding: 16px;
        margin-bottom: 16px;
    }

    .page-header h1 {
        font-size: 1.5rem;
    }
}
</style>
